import { createElement, useEffect } from "react";

export const DEFAULT_HAIRBOOK_EMBED_ORIGIN = "https://embed.hairbook.dev";

export type HairbookEmbedSource = "legacy" | "shin";
export type HairbookEmbedKind = "knowMore" | "styles" | "staff";

/**
 * Two salonjobs embed families on the same host (`embed.js`).
 * hairbook-pages chooses the family. The Worker does not detect source.
 * Each tag is a full section (header + body). pages only mounts the element.
 */
export const HAIRBOOK_EMBED_TAGS = {
  legacy: {
    knowMore: "legacy-hb-know-more",
    styles: "legacy-hb-styles",
    staff: "legacy-hb-staff",
  },
  shin: {
    knowMore: "shin-hb-know-more",
    styles: "shin-hb-styles",
    staff: "shin-hb-staff",
  },
} as const;

export type HairbookEmbedTag =
  (typeof HAIRBOOK_EMBED_TAGS)[HairbookEmbedSource][HairbookEmbedKind];

/**
 * Fields the switch may read. salonjobs show today only guarantees `id`
 * (numeric Hairbook salon id from `show.sql`). The rest are optional so a
 * later SQL / toggle column can flip the family without changing callers.
 */
export type EmbedSalonInput = {
  id?: string | number | null;
  hpbSalonId?: string | null;
  hpb_salon_id?: string | null;
  embedSource?: string | null;
  embed_source?: string | null;
  instagramUrl?: string | null;
  instagram_url?: string | null;
};

const INSTAGRAM_HOST = /(^|\.)instagram\.com$/i;
const INSTAGRAM_RESERVED_PATHS = new Set([
  "p",
  "reel",
  "reels",
  "tv",
  "stories",
  "explore",
  "accounts",
  "direct",
  "about",
  "legal",
  "developer",
  "directory",
  "lite",
]);

/**
 * Profile username from an Instagram URL. Query strings and reserved
 * paths (`/p/…`, `/reel/…`) are dropped so the Worker does not guess.
 */
export function instagramUserFromUrl(url: string | null | undefined): string | null {
  const raw = String(url ?? "").trim();
  if (!raw) return null;

  try {
    const parsed = new URL(raw.includes("://") ? raw : `https://${raw}`);
    if (!/^https?:$/.test(parsed.protocol) || !INSTAGRAM_HOST.test(parsed.hostname)) return null;
    const segment = parsed.pathname.split("/").filter(Boolean)[0];
    if (!segment) return null;
    const user = decodeURIComponent(segment).replace(/^@/, "");
    if (!user || INSTAGRAM_RESERVED_PATHS.has(user.toLowerCase())) return null;
    if (!/^[\w.]{1,30}$/.test(user)) return null;
    return user.toLowerCase();
  } catch {
    return null;
  }
}

export type EmbedSourceOverride = {
  /** Caller override. Wins over salon.embedSource and the id-shape heuristic. */
  source?: string | null;
};

export type HairbookEmbedDocument = {
  querySelector: (selector: string) => unknown;
  createElement: (tagName: string) => { type: string; src: string };
  head: { appendChild: (node: { type: string; src: string }) => void };
};

const SALONPAGE_ID_SHAPE = /^H\d+$/;

export function resolveHairbookEmbedOrigin(override?: string | null): string {
  const env = import.meta.env as { VITE_HAIRBOOK_EMBED_ORIGIN?: string };
  const raw = override || env.VITE_HAIRBOOK_EMBED_ORIGIN || DEFAULT_HAIRBOOK_EMBED_ORIGIN;
  return String(raw).replace(/\/$/, "");
}

export function hairbookEmbedScriptUrl(origin = resolveHairbookEmbedOrigin()): string {
  return `${origin}/embed.js`;
}

export function isSalonpageSalonId(value: string | number | null | undefined): boolean {
  return SALONPAGE_ID_SHAPE.test(String(value ?? "").trim());
}

function normalizeEmbedSource(value: string | null | undefined): HairbookEmbedSource | null {
  if (value === "legacy" || value === "shin") return value;
  if (value === "salonpage") return "shin";
  return null;
}

/**
 * Decide which embed family to mount for a salonjobs salon.
 *
 * Investigated (not used as an implicit switch — none is on salonjobs show props):
 * - salonjobs Turso `feature_toggles`: `salonjobs_visibility`, `salon_published`,
 *   `listed`. These are Hairbook / salonjobs publication, not シンサロン.
 * - salonpage `page_published` lives on salonpage Turso (`target_id` = `H000…`)
 *   and is not joined into `salonjobs/show.sql`.
 * - `hpb_salon_id` on `salon_jobs` means "has a HotPepper store", not
 *   "has a published シンサロン page". Presence must not flip the family.
 *
 * Heuristic (first match wins):
 * 1. `options.source` — explicit caller override (`legacy` | `shin`; `salonpage` aliases `shin`)
 * 2. `salon.embedSource` / `salon.embed_source` — same, for a future SQL column
 * 3. `salon.id` matching `H` + digits (シンサロン / HPB id shape) → `shin`
 * 4. otherwise → `legacy` (current numeric Hairbook salon ids)
 */
export function embedSourceForSalon(
  salon: EmbedSalonInput,
  options: EmbedSourceOverride = {},
): HairbookEmbedSource {
  return (
    normalizeEmbedSource(options.source) ??
    normalizeEmbedSource(salon.embedSource) ??
    normalizeEmbedSource(salon.embed_source) ??
    (isSalonpageSalonId(salon.id) ? "shin" : "legacy")
  );
}

export function hairbookEmbedSalonId(
  salon: EmbedSalonInput,
  source: HairbookEmbedSource = embedSourceForSalon(salon),
): string {
  if (source === "shin") {
    const hpbId = salon.hpbSalonId ?? salon.hpb_salon_id;
    if (isSalonpageSalonId(hpbId)) return String(hpbId).trim();
    if (isSalonpageSalonId(salon.id)) return String(salon.id).trim();
  }
  return String(salon.id ?? "");
}

export function hairbookEmbedTag(
  source: HairbookEmbedSource,
  kind: HairbookEmbedKind,
): HairbookEmbedTag {
  return HAIRBOOK_EMBED_TAGS[source][kind];
}

export function salonjobsHairbookEmbeds(
  salon: EmbedSalonInput,
  options: EmbedSourceOverride = {},
): {
  source: HairbookEmbedSource;
  salonId: string;
  tags: Record<HairbookEmbedKind, HairbookEmbedTag>;
} {
  const source = embedSourceForSalon(salon, options);
  return {
    source,
    salonId: hairbookEmbedSalonId(salon, source),
    tags: {
      knowMore: hairbookEmbedTag(source, "knowMore"),
      styles: hairbookEmbedTag(source, "styles"),
      staff: hairbookEmbedTag(source, "staff"),
    },
  };
}

export function hairbookEmbedElementProps(salonId: string | number): { "salon-id": string } {
  return { "salon-id": String(salonId) };
}

export function ensureHairbookEmbedScript(
  origin?: string,
  doc: HairbookEmbedDocument = document as unknown as HairbookEmbedDocument,
): void {
  const src = hairbookEmbedScriptUrl(origin ?? resolveHairbookEmbedOrigin());
  if (doc.querySelector(`script[src="${src}"]`)) return;
  const script = doc.createElement("script");
  script.type = "module";
  script.src = src;
  doc.head.appendChild(script);
}

function useHairbookEmbedScript() {
  useEffect(() => {
    ensureHairbookEmbedScript();
  }, []);
}

export function HairbookEmbed({
  source,
  kind,
  salonId,
}: {
  source: HairbookEmbedSource;
  kind: HairbookEmbedKind;
  salonId: string | number;
}) {
  useHairbookEmbedScript();
  return createElement(hairbookEmbedTag(source, kind), hairbookEmbedElementProps(salonId));
}

function mountKind(kind: HairbookEmbedKind, salon: EmbedSalonInput, options?: EmbedSourceOverride) {
  const embed = salonjobsHairbookEmbeds(salon, options);
  return createElement(HairbookEmbed, {
    source: embed.source,
    kind,
    salonId: embed.salonId,
  });
}

export function HairbookKnowMoreEmbed({
  salon,
  source,
}: {
  salon: EmbedSalonInput;
  source?: HairbookEmbedSource;
}) {
  return mountKind("knowMore", salon, { source });
}

export function HairbookStylesEmbed({
  salon,
  source,
}: {
  salon: EmbedSalonInput;
  source?: HairbookEmbedSource;
}) {
  return mountKind("styles", salon, { source });
}

export function HairbookStaffEmbed({
  salon,
  source,
}: {
  salon: EmbedSalonInput;
  source?: HairbookEmbedSource;
}) {
  return mountKind("staff", salon, { source });
}

export function HairbookInstagramEmbed({ salon }: { salon: EmbedSalonInput }) {
  useHairbookEmbedScript();
  const rawUrl = (salon.instagramUrl ?? salon.instagram_url)?.trim();
  if (!rawUrl) return null;
  let url: URL;
  try {
    url = new URL(rawUrl.includes("://") ? rawUrl : `https://${rawUrl}`);
    if (!/^https?:$/.test(url.protocol)) return null;
  } catch {
    return null;
  }
  const username = instagramUserFromUrl(url.href);
  const link = createElement("a", {
    href: url.href,
    target: "_blank",
    rel: "noopener noreferrer",
    className: "inline-flex items-center justify-center px-6 h-12 rounded-full bg-black text-white text-sm",
  }, "Instagram でフォロー ↗");
  // 未定義custom element / JS失敗時もlight DOMのリンクを残す。
  // URLが短縮・リダイレクト形式なら逆引きせずリンクだけを表示する。
  return username ? createElement("hb-instagram", { "instagram-user": username }, link) : link;
}
